import { PricingProductOptionsGroup } from '../../../store/features/products/types';
import { useTranslation } from 'react-i18next';
import { Path, PathValue } from 'react-hook-form';
import { PricingPackagesFormValues } from '../../../store/features/packages/types';

export const OPTIONS_VALIDATION_MESSAGE = 'VALIDATION_ERROR';

export interface UseMultipleOptionsValidationProps {
  optionGroup: PricingProductOptionsGroup;
}
export function useMultipleOptionsValidation(
  props: UseMultipleOptionsValidationProps,
) {
  const {
    optionGroup: { min: minOptionsToSelect, max: maxOptionsToSelect },
  } = props;
  const { t } = useTranslation();

  const isMinMax = minOptionsToSelect && maxOptionsToSelect;
  const isOnlyMin = minOptionsToSelect && !maxOptionsToSelect;
  const isOnlyMax = !minOptionsToSelect && maxOptionsToSelect;
  const isMinMaxEqual = minOptionsToSelect === maxOptionsToSelect;

  const getOptionsListHeading = () => {
    if (isMinMax && isMinMaxEqual) {
      return t(
        maxOptionsToSelect === 1
          ? 'site.pricing.products-selection.multiple-options-list.equal.heading.single'
          : 'site.pricing.products-selection.multiple-options-list.equal.heading.plural',
        {
          0: maxOptionsToSelect,
        },
      );
    }

    if (isMinMax) {
      return t(
        'site.pricing.products-selection.multiple-options-list.min-and-max.heading',
        {
          0: minOptionsToSelect,
          1: maxOptionsToSelect,
        },
      );
    }

    if (isOnlyMin) {
      return t(
        'site.pricing.products-selection.multiple-options-list.min.heading',
        {
          0: minOptionsToSelect,
        },
      );
    }

    if (isOnlyMax) {
      return t(
        'site.pricing.products-selection.multiple-options-list.max.heading',
        {
          0: maxOptionsToSelect,
        },
      );
    }

    return t(
      'site.pricing.products-selection.multiple-options-list.select-options.heading',
    );
  };

  const processMinMaxEqualValidation = (
    selectedOptionsCount: number,
    maxValue: number,
  ) => {
    const optionsToSelectCount = maxValue - selectedOptionsCount;

    if (selectedOptionsCount === 0) {
      return t(
        maxOptionsToSelect === 1
          ? 'site.pricing.products-selection.multiple-options-list.equal.heading.single'
          : 'site.pricing.products-selection.multiple-options-list.equal.heading.plural',
        { 0: maxOptionsToSelect },
      );
    }

    return t(
      optionsToSelectCount === 1
        ? 'site.pricing.products-selection.multiple-options-list.min-and-max-equal.validation.single'
        : 'site.pricing.products-selection.multiple-options-list.min-and-max-equal.validation.plural',
      { 0: optionsToSelectCount },
    );
  };

  const validationHandler = (
    value: PathValue<
      PricingPackagesFormValues,
      Path<PricingPackagesFormValues>
    >,
  ) => {
    const selectedOptionsCount = (Array.isArray(value) && value.length) || 0;

    if (isMinMax) {
      if (isMinMaxEqual && selectedOptionsCount < maxOptionsToSelect) {
        return processMinMaxEqualValidation(
          selectedOptionsCount,
          maxOptionsToSelect,
        );
      }

      return (
        (selectedOptionsCount >= minOptionsToSelect &&
          selectedOptionsCount <= maxOptionsToSelect) ||
        OPTIONS_VALIDATION_MESSAGE
      );
    }

    if (isOnlyMin) {
      return (
        selectedOptionsCount >= minOptionsToSelect || OPTIONS_VALIDATION_MESSAGE
      );
    }

    if (isOnlyMax) {
      return (
        selectedOptionsCount <= maxOptionsToSelect || OPTIONS_VALIDATION_MESSAGE
      );
    }

    return true;
  };

  return {
    listHeading: getOptionsListHeading(),
    validationHandler,
  };
}
